import { useCallback, useMemo } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { DialogAtendimentoEncerrado } from '@/components/dialog-atendimento-encerrado'
import { useCriarAtendimento } from '@/hooks/use-criar-atendimento'
import { useEncerrarAtendimento } from '@/hooks/use-encerrar-atendimento'
import { useLocalStorage } from '@/hooks/use-local-storage'
import { useCountryNavigate } from '@/hooks/use-localized-navigate'
import { showToast } from '@/infra/sonner'
import type { TAtendimento, TCliente } from '@/types/atendimento'
import { atendimentoKeys } from '@/utils/atendimento-query-keys'
import { oportunidadesChileKeys } from '@/utils/oportunidades-chile-query-keys'

const CLIENTE_INATIVO = 'INACTIVE_CLIENT'
const OPPORTUNITY_CLOSED = 'OPPORTUNITY_CLOSED'

export const useAtendimento = <T extends TCliente = TCliente>() => {
  const [atendimento, setAtendimento] = useLocalStorage<TAtendimento<T> | null>('atendimento', null)
  const { mutateAsync: criarAtendimento, isPending: isCriandoAtendimento } = useCriarAtendimento()
  const queryClient = useQueryClient()
  const navigate = useCountryNavigate()
  const { t } = useTranslation(['common', 'atendimento'])
  const { mutateAsync: encerrarAtendimento } = useEncerrarAtendimento()

  const handleIniciarAtendimento = useCallback(
    async (clienteId?: string | null, onSuccess?: VoidFunction, codigoOportunidade?: string) => {
      await criarAtendimento(
        { clienteId, codigoOportunidade },
        {
          onSuccess: (res) => {
            setAtendimento(res.data as TAtendimento<T>)
            onSuccess?.()
          },
          onError: async (error) => {
            const erroClienteInativo = error?.response?.data?.find((e) => e.code === CLIENTE_INATIVO)
            if (erroClienteInativo) {
              showToast({
                message: t('atendimento:errors.cliente_inativo'),
                variant: 'error',
              })
              return
            }

            const erroOportunidadeFechada = error?.response?.data?.find((e) => e.code === OPPORTUNITY_CLOSED)
            if (erroOportunidadeFechada) {
              const message = t('atendimento:errors.opportunity_closed')
              showToast({
                message,
                variant: 'error',
              })
              await queryClient.invalidateQueries({
                queryKey: oportunidadesChileKeys.oportunidade,
              })
              return
            }

            showToast({
              message: t('errors_gerais.erro_interno'),
              variant: 'error',
            })
          },
        },
      )
    },
    [criarAtendimento, queryClient, setAtendimento, t],
  )

  const handleEncerrarAtendimento = useCallback(
    async (pathRedirecionamento = '/', exibirModalPadrao = true) => {
      if (!atendimento) return
      await encerrarAtendimento(atendimento.id, {
        onSuccess: (response) => {
          queryClient.invalidateQueries({ queryKey: atendimentoKeys.ultimosAtendimentos() })
          queryClient.invalidateQueries({ queryKey: atendimentoKeys.ultimosAtendimentosChile() })
          if (exibirModalPadrao) {
            DialogAtendimentoEncerrado.show({
              duracao: response?.data.duracao,
              nome: atendimento.cliente.nome,
              imagem: atendimento.cliente.$type === 'Brasil' ? atendimento.cliente.imagem : null,
            })
          }

          setAtendimento(null)
          navigate(pathRedirecionamento)
        },
        onError: (error) => {
          if (error.response?.data.some((err) => err.message.includes('já está encerrado'))) {
            showToast({ message: t('atendimento:errors.ja_encerrado'), variant: 'info' })
            setAtendimento(null)
            return
          }
          showToast({ message: t('errors_gerais.erro_interno'), variant: 'error' })
        },
      })
    },
    [atendimento, encerrarAtendimento, navigate, queryClient, setAtendimento, t],
  )

  const value = useMemo(
    () => ({
      atendimento,
      atualizarAtendimento: setAtendimento,
      handleIniciarAtendimento,
      handleEncerrarAtendimento,
      isIniciandoAtendimento: isCriandoAtendimento,
      isEmAtendimento: Boolean(atendimento?.cliente?.id),
    }),
    [atendimento, handleEncerrarAtendimento, handleIniciarAtendimento, isCriandoAtendimento, setAtendimento],
  )
  return value
}
